import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import ControlledCheckBox from 'src/shared/components/form/controlledCheckbox';
import { FundraiserSettingData } from '../models/fundraiserResponse';

type FundraiserDetailSettingsSectionProps = {
  settingData: FundraiserSettingData;
};

export const FundraiserSettingsDetailSection = (props: FundraiserDetailSettingsSectionProps) => {
  const { settingData } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(true);

  const fundraiserSettingsSchema = yup.object().shape({
    customHeading: yup.boolean().required(),
    disallowAnonymous: yup.string().oneOf(['0', '1']).required(),
    displayAddressFields: yup.boolean().required(),
    displayMessageField: yup.boolean().required(),
    publicUrl: yup.string().optional(),
    destinationList: yup.string().optional(),
    mainHeadingVisible: yup.boolean().required(),
    isAdminForm: yup.boolean().required(),
    displayInvoiceInPayment: yup.string().oneOf(['0', '1']).required(),
    displayCompanyFields: yup.string().oneOf(['0', '1']).required(),
    disallowHonoredName: yup.string().oneOf(['0', '1']).required(),
    disallowFromName: yup.string().oneOf(['0', '1']).required(),
  });

  const formMethods = useForm<FundraiserSettingData>({
    resolver: yupResolver(fundraiserSettingsSchema),
    defaultValues: {
      customHeading: settingData.customHeading,
      disallowAnonymous: settingData.disallowAnonymous ? '1' : '0', // Konverteras till 1 & 0 pga checkboxkomponenten
      displayAddressFields: settingData.displayAddressFields ? '1' : '0',
      displayMessageField: settingData.displayMessageField, // Används ej idag
      publicUrl: settingData.publicUrl || '',
      destinationList: settingData.destinationList || '',
      mainHeadingVisible: settingData.mainHeadingVisible,
      isAdminForm: settingData.isAdminForm,
      displayInvoiceInPayment: settingData.displayInvoiceInPayment ? '1' : '0',
      displayCompanyFields: settingData.displayCompanyFields ? '1' : '0',
      disallowHonoredName: settingData.disallowHonoredName ? '1' : '0',
      disallowFromName: settingData.disallowFromName ? '1' : '0',
    },
  });

  const { handleSubmit, watch } = formMethods;
  const invInPay = watch('displayInvoiceInPayment');
  const disallowAnonym = watch('disallowAnonymous');
  const displayAddress = watch('displayAddressFields');
  const displayCompany = watch('displayCompanyFields');
  const disallowHonored = watch('disallowHonoredName');
  const disallowFrom = watch('disallowFromName');

  const onSubmit = (data: FundraiserSettingData) => {
    setLoading(true);

    // Mappa tillbaka till boolean innan post
    const dataToPost: FundraiserSettingData = {
      ...data,
      displayInvoiceInPayment: data.displayInvoiceInPayment === '1',
      displayCompanyFields: data.displayInvoiceInPayment === '1',
      disallowHonoredName: data.displayInvoiceInPayment === '1',
      disallowFromName: data.displayInvoiceInPayment === '1',
      disallowAnonymous: data.displayInvoiceInPayment === '1',
      displayAddressFields: data.displayInvoiceInPayment === '1',
    };
    ConsoleHelper.log('dataToPost: ', dataToPost);

    try {
      //   const res = await updateFundraiserSettings(data);
      if (isMounted()) {
        toast.success(<>{t('fundraiser:fundraiserDetailSettingsSection.postSuccess')}</>);
      }
    } catch (e: any) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('fundraiser:fundraiserDetailSettingsSection.postError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
              {t('fundraiser:fundraiserDetailSettingsSection.settings')}
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
              {t('fundraiser:fundraiserDetailSettingsSection.formAndPayment')}
            </Typography>
            {/* Checkboxarna ska bara visas om propertyn finns */}
            {settingData?.displayInvoiceInPayment !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.displayInvoiceInPayment')}
                name='displayInvoiceInPayment'
                value={invInPay}
              />
            )}
            {settingData?.displayAddressFields !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.displayAddressFields')}
                name='displayAddressFields'
                value={displayAddress}
              />
            )}
            {settingData?.displayCompanyFields !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.displayCompanyFields')}
                name='displayCompanyFields'
                value={displayCompany}
              />
            )}
            {settingData?.disallowAnonymous !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.disallowAnonymous')}
                name='disallowAnonymous'
                value={disallowAnonym}
              />
            )}
            {settingData?.disallowHonoredName !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.disallowHonoredName')}
                name='disallowHonoredName'
                value={disallowHonored}
              />
            )}
            {settingData?.disallowFromName !== undefined && (
              <ControlledCheckBox
                clickOnlyOnBox
                disabled
                label={t('fundraiser:fundraiserDetailSettingsSection.disallowFromName')}
                name='disallowFromName'
                value={disallowFrom}
              />
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
